import { type MouseEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  hasActiveSubscription,
  isCoach,
  isPerformActiveCoach,
} from "../../utils/user-helpers";
import { getUserToRequest } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import Modal from "../modal-new";
import { isAnnouncementPending } from "./ClientConfigAnnouncementModal";

const SEEN_KEY = "coachCheckinCompleted";
// Coaches created after this date are brand new, not part of the check-in audience.
const LAUNCH_DATE = new Date("2026-10-01T00:00:00Z");
const TYPEFORM_ID = "NHfEOQja";
const GOOGLE_REVIEW_URL =
  "https://www.google.com/search?q=mystrengthbook#lrd=0x53716f6e9ed6d017:0x341ff39ef06ec49c,3";

// The user schema has no createdAt; an ObjectId's first 4 bytes are its creation time.
const createdBeforeLaunch = (user) =>
  parseInt(String(user._id).slice(0, 8), 16) * 1000 < LAUNCH_DATE.getTime();

// Tiers come from the account that pays (the org owner for sub-coaches).
const getProducts = (owner) =>
  [
    hasActiveSubscription(owner) && "pull",
    owner.pushAccountInfo && "push",
    owner.pressAccountInfo && "press",
    isPerformActiveCoach(owner) && "perform",
  ].filter(Boolean) as string[];

const svgProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const ClipboardCheckIcon = () => (
  <svg aria-hidden="true" {...svgProps}>
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </svg>
);

const StarIcon = () => (
  <svg aria-hidden="true" {...svgProps}>
    <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.3-6.2 3.3L8 14.2l-5-4.9 6.9-1z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg aria-hidden="true" {...svgProps} width={18} height={18}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg aria-hidden="true" {...svgProps} width={18} height={18}>
    <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
);

const markSeen = () =>
  fetch("/actions/user/video-shown", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: SEEN_KEY }),
  });

const Typeform = ({ hidden, onSubmit }) => {
  const ref = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the widget must mount once; hidden fields and the handler are fixed for this modal's lifetime
  useEffect(() => {
    let unmount = () => {};
    let cancelled = false;
    // Loaded on demand: the SDK touches `window`, and this file is also compiled for SSR.
    import("@typeform/embed").then(({ createWidget }) => {
      if (cancelled) return;
      unmount = createWidget(TYPEFORM_ID, {
        container: ref.current,
        hidden,
        onSubmit,
        // Takes over the ending-screen button, which would otherwise navigate inside the iframe.
        onEndingButtonClick: () =>
          window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer"),
        inlineOnMobile: true,
      }).unmount;
    });
    return () => {
      cancelled = true;
      unmount();
    };
  }, []);

  return <div ref={ref} className="checkin-typeform" />;
};

export const CoachCheckinModal = ({ activeUser, organizationOwner }) => {
  const { t } = useTranslation("coaching-portal");
  const owner = activeUser && getUserToRequest(activeUser, organizationOwner);
  const products = owner && isCoach(activeUser) ? getProducts(owner) : [];
  const [isVisible, setIsVisible] = useState(
    () =>
      products.length > 0 &&
      createdBeforeLaunch(activeUser) &&
      !activeUser.welcomeVideos?.[SEEN_KEY] &&
      // One modal at a time: the announcement goes first, this one next visit.
      !isAnnouncementPending(activeUser)
  );
  const [isStarted, setIsStarted] = useState(false);

  if (!isVisible) return null;

  // Closing without submitting counts as completed too, so it never nags.
  const handleClose = (e: MouseEvent) => {
    e.preventDefault();
    setIsVisible(false);
    markSeen();
  };

  const [firstName = "", ...rest] = (activeUser.name || "").trim().split(" ");

  return (
    <Modal
      className={
        isStarted ? "coach-checkin-modal started" : "coach-checkin-modal"
      }
      header={false}
      onClose={handleClose}
    >
      <div className="checkin-content">
        <button
          type="button"
          className="checkin-close"
          data-button-type="tertiary"
          data-icon-only="true"
          aria-label={t("coachCheckinModal.closeAria")}
          onClick={handleClose}
        >
          <img src="/img/new-icons/red-cross.svg" alt="" />
        </button>
        {isStarted ? (
          <Typeform
            hidden={{
              account_id: String(activeUser._id),
              email: activeUser.email || "",
              first_name: firstName,
              last_name: rest.join(" "),
              company: activeUser.businessName || owner.businessName || "",
              plan: products.join(","),
              source: "in_app_modal",
            }}
            onSubmit={markSeen}
          />
        ) : (
          <>
            <h2>{t("coachCheckinModal.title")}</h2>
            <p className="checkin-intro">{t("coachCheckinModal.intro")}</p>
            <section className="checkin-card" data-primary="true">
              <span className="checkin-icon" data-tone="info">
                <ClipboardCheckIcon />
              </span>
              <div>
                <h3>
                  {t("coachCheckinModal.checkinTitle")}
                  <span>{t("coachCheckinModal.minutes")}</span>
                </h3>
                <p>{t("coachCheckinModal.checkinBody")}</p>
                <button
                  type="button"
                  data-button-type="primary"
                  onClick={() => setIsStarted(true)}
                >
                  {t("coachCheckinModal.checkinCta")}
                  <ArrowRightIcon />
                </button>
              </div>
            </section>
            <section className="checkin-card">
              <span className="checkin-icon" data-tone="warning">
                <StarIcon />
              </span>
              <div>
                <h3>{t("coachCheckinModal.reviewTitle")}</h3>
                <p>{t("coachCheckinModal.reviewBody")}</p>
                {/* biome-ignore lint/a11y/useSemanticElements: a link that opens a new tab; role only picks up button styles */}
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="button"
                  data-button-type="secondary"
                >
                  {t("coachCheckinModal.reviewCta")}
                  <ExternalLinkIcon />
                </a>
              </div>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
};
